import React from "react";

/**
 * Cinematic vial — cyberpunk laboratory aesthetic.
 * Inspired by neon biotech reference image:
 *  - Hexagonal honeycomb backdrop (neon)
 *  - Atmospheric fog/smoke
 *  - Neon base ring with glow
 *  - Realistic glass vial with vibrant liquid
 *  - Floating holographic HUD (ECG + label)
 *  - Surface reflection with circuit traces
 */
const VialSvg = ({ seed = 1, color = "#00DFC8", label = "", className = "", "aria-label": ariaLabel }) => {
  const uid = `cv${seed}-${color.replace(/[^a-z0-9]/gi, "")}`;
  const accent = color;
  const accent2 = seed % 2 === 0 ? "#FF3D9A" : "#A855F7"; // magenta/violet secondary accent
  const hexAccent = "#00DF82";

  // Build initials
  const init = React.useMemo(() => {
    if (!label) return "PEP";
    const clean = label.replace(/[+/]/g, " ").replace(/-/g, "");
    const words = clean.split(/\s+/).filter(Boolean);
    if (words.length === 1) return words[0].slice(0, 3).toUpperCase();
    return words.slice(0, 3).map((w) => w[0]).join("").toUpperCase();
  }, [label]);

  // Hex honeycomb coordinates (back layer)
  const hexes = React.useMemo(() => {
    const list = [];
    const r = 14, w = r * Math.sqrt(3), h = r * 1.5;
    for (let row = -2; row < 6; row++) {
      for (let col = -1; col < 6; col++) {
        const x = col * w + (row % 2 ? w / 2 : 0);
        const y = row * h;
        list.push({ x, y });
      }
    }
    return { list, r };
  }, []);

  // ECG path inside the HUD
  const ecgPath = "M 0 8 L 8 8 L 12 5 L 16 11 L 20 4 L 22 12 L 26 8 L 70 8";

  // Rising bubbles
  const bubbles = [
    { x: 96, delay: 0, size: 1.4, dur: 4.8 },
    { x: 108, delay: 1.3, size: 1.8, dur: 5.5 },
    { x: 102, delay: 2.2, size: 1.1, dur: 4.2 },
    { x: 113, delay: 0.8, size: 1.3, dur: 5.0 },
  ];

  return (
    <svg viewBox="0 0 220 220" className={className} xmlns="http://www.w3.org/2000/svg" role={ariaLabel ? "img" : undefined} aria-label={ariaLabel} aria-hidden={ariaLabel ? undefined : "true"}>
      {ariaLabel ? <title>{ariaLabel}</title> : null}
      <defs>
        {/* Soft glow filter */}
        <filter id={`glow-${uid}`} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="3" result="b" />
          <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        <filter id={`bigglow-${uid}`} x="-100%" y="-100%" width="300%" height="300%">
          <feGaussianBlur stdDeviation="6" />
        </filter>
        <filter id={`smoke-${uid}`} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="8" />
        </filter>

        {/* Liquid gradient with internal glow */}
        <linearGradient id={`liq-${uid}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={accent} stopOpacity="0.85" />
          <stop offset="50%" stopColor={accent} stopOpacity="0.95" />
          <stop offset="100%" stopColor={accent} stopOpacity="0.7" />
        </linearGradient>

        {/* Glass body — very subtle, mostly transparent */}
        <linearGradient id={`glass-${uid}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#1A2230" stopOpacity="0.18" />
          <stop offset="50%" stopColor="#0A0F18" stopOpacity="0.05" />
          <stop offset="100%" stopColor="#1A2230" stopOpacity="0.22" />
        </linearGradient>

        {/* Aluminum collar */}
        <linearGradient id={`metal-${uid}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#2A2A35" />
          <stop offset="40%" stopColor="#C8CDD4" />
          <stop offset="60%" stopColor="#FFFFFF" />
          <stop offset="100%" stopColor="#2A2A35" />
        </linearGradient>

        {/* Rubber stopper */}
        <linearGradient id={`rubber-${uid}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#1A1A22" />
          <stop offset="100%" stopColor="#08080C" />
        </linearGradient>

        {/* Neon base ring */}
        <radialGradient id={`base-${uid}`} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={accent2} stopOpacity="0.9" />
          <stop offset="100%" stopColor={accent2} stopOpacity="0" />
        </radialGradient>

        {/* Smoke clouds */}
        <radialGradient id={`smoke1-${uid}`} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={accent} stopOpacity="0.25" />
          <stop offset="100%" stopColor={accent} stopOpacity="0" />
        </radialGradient>
        <radialGradient id={`smoke2-${uid}`} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={accent2} stopOpacity="0.22" />
          <stop offset="100%" stopColor={accent2} stopOpacity="0" />
        </radialGradient>

        {/* Surface reflection */}
        <linearGradient id={`refl-${uid}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={accent} stopOpacity="0.35" />
          <stop offset="100%" stopColor={accent} stopOpacity="0" />
        </linearGradient>

        {/* Light streak */}
        <linearGradient id={`shine-${uid}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0" />
          <stop offset="50%" stopColor="#FFFFFF" stopOpacity="0.4" />
          <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
        </linearGradient>

        {/* Clip mask for liquid (inside glass body) */}
        <clipPath id={`liqclip-${uid}`}>
          <path d="M 80 80 L 80 160 Q 80 170 90 170 L 130 170 Q 140 170 140 160 L 140 80 Z" />
        </clipPath>
      </defs>

      {/* === LAYER 1: Background hex honeycomb (faded, perspective) === */}
      <g opacity="0.32" stroke={hexAccent} strokeWidth="0.6" fill="none">
        {hexes.list.map((hx, i) => {
          // distance from center, used to fade outer hexes
          const dx = hx.x - 90;
          const dy = hx.y - 80;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d > 110) return null;
          const op = Math.max(0.15, 1 - d / 110);
          const pts = Array.from({ length: 6 }, (_, k) => {
            const ang = (Math.PI / 3) * k - Math.PI / 6;
            return `${hx.x + hexes.r * Math.cos(ang)},${hx.y + hexes.r * Math.sin(ang)}`;
          }).join(" ");
          return <polygon key={i} points={pts} opacity={op} />;
        })}
      </g>

      {/* === LAYER 2: Atmospheric smoke === */}
      <ellipse cx="60" cy="60" rx="55" ry="40" fill={`url(#smoke1-${uid})`} filter={`url(#smoke-${uid})`}>
        <animate attributeName="cx" values="60;90;60" dur="14s" repeatCount="indefinite" />
      </ellipse>
      <ellipse cx="160" cy="100" rx="45" ry="35" fill={`url(#smoke2-${uid})`} filter={`url(#smoke-${uid})`}>
        <animate attributeName="cx" values="160;130;160" dur="16s" repeatCount="indefinite" />
      </ellipse>

      {/* === LAYER 3: Holographic HUD above vial === */}
      <g transform="translate(75 18)" opacity="0.9">
        {/* outer frame */}
        <rect x="0" y="0" width="80" height="28" fill="rgba(0,223,200,0.05)" stroke={accent} strokeOpacity="0.7" strokeWidth="0.6" />
        {/* corner accents */}
        {[[0, 0], [80, 0], [0, 28], [80, 28]].map(([x, y], i) => (
          <g key={i} transform={`translate(${x} ${y})`} stroke={accent} strokeWidth="1">
            <line x1={x === 0 ? 0 : -4} y1="0" x2={x === 0 ? 4 : 0} y2="0" />
            <line x1="0" y1={y === 0 ? 0 : -4} x2="0" y2={y === 0 ? 4 : 0} />
          </g>
        ))}
        {/* ECG line */}
        <path d={ecgPath} stroke={accent} strokeWidth="0.9" fill="none" filter={`url(#glow-${uid})`}>
          <animate attributeName="stroke-dasharray" values="0 80;80 0" dur="3s" repeatCount="indefinite" />
        </path>
        {/* mini bars/data on right */}
        <g transform="translate(58 14)" fill={accent} opacity="0.7">
          {[3, 5, 2, 6, 4, 7].map((h, i) => (
            <rect key={i} x={i * 3} y={8 - h} width="2" height={h}>
              <animate attributeName="height" values={`${h};${h + 2};${h}`} dur={`${1.5 + i * 0.2}s`} repeatCount="indefinite" />
              <animate attributeName="y" values={`${8 - h};${6 - h};${8 - h}`} dur={`${1.5 + i * 0.2}s`} repeatCount="indefinite" />
            </rect>
          ))}
        </g>
        {/* connection line from HUD to vial */}
        <line x1="40" y1="28" x2="40" y2="40" stroke={accent} strokeOpacity="0.4" strokeWidth="0.5" strokeDasharray="2 2" />
      </g>

      {/* === LAYER 4: Surface circuit traces === */}
      <g stroke={accent} strokeOpacity="0.35" strokeWidth="0.5" fill="none">
        <path d="M 20 180 L 50 180 L 55 175 L 80 175" />
        <path d="M 200 180 L 170 180 L 165 175 L 140 175" />
        <circle cx="50" cy="180" r="1.5" fill={accent} opacity="0.6" />
        <circle cx="170" cy="180" r="1.5" fill={accent} opacity="0.6" />
      </g>

      {/* === LAYER 5: Neon base ring with glow === */}
      <ellipse cx="110" cy="178" rx="55" ry="7" fill={`url(#base-${uid})`} filter={`url(#bigglow-${uid})`} />
      <ellipse cx="110" cy="178" rx="48" ry="4.5" fill="none" stroke={accent2} strokeWidth="1.5" opacity="0.95" filter={`url(#glow-${uid})`}>
        <animate attributeName="opacity" values="0.6;1;0.6" dur="2.6s" repeatCount="indefinite" />
      </ellipse>
      <ellipse cx="110" cy="178" rx="42" ry="3.5" fill="none" stroke={accent} strokeWidth="0.8" opacity="0.7" />

      {/* === LAYER 6: Reflection beneath vial === */}
      <g opacity="0.5" transform="translate(0 175) scale(1 -0.45) translate(0 -175)">
        <path
          d="M 80 80 L 80 160 Q 80 170 90 170 L 130 170 Q 140 170 140 160 L 140 80 Z"
          fill={`url(#refl-${uid})`}
        />
      </g>

      {/* === LAYER 7: The glass vial === */}
      {/* Stopper */}
      <rect x="92" y="32" width="36" height="14" rx="2" fill={`url(#rubber-${uid})`} />
      <rect x="92" y="32" width="36" height="2" fill="#FFFFFF" opacity="0.15" />
      {/* Aluminum collar */}
      <rect x="88" y="44" width="44" height="9" fill={`url(#metal-${uid})`} />
      <rect x="88" y="44" width="44" height="1" fill="#FFFFFF" opacity="0.6" />
      <rect x="88" y="51.5" width="44" height="1.5" fill="#000000" opacity="0.55" />
      {/* Neck */}
      <rect x="94" y="53" width="32" height="6" fill={`url(#glass-${uid})`} stroke={accent} strokeOpacity="0.35" strokeWidth="0.5" />
      {/* Glass body (slim cylindrical) */}
      <path
        d="M 80 59 L 80 162 Q 80 172 90 172 L 130 172 Q 140 172 140 162 L 140 59 Z"
        fill={`url(#glass-${uid})`}
        stroke={accent}
        strokeOpacity="0.65"
        strokeWidth="0.9"
        filter={`url(#glow-${uid})`}
      />

      {/* === LAYER 8: Liquid inside === */}
      <g clipPath={`url(#liqclip-${uid})`}>
        {/* Body of liquid */}
        <rect x="80" y="105" width="60" height="70" fill={`url(#liq-${uid})`}>
          <animate attributeName="y" values="105;107;105" dur="3s" repeatCount="indefinite" />
        </rect>
        {/* Liquid surface wave */}
        <path d="M 80 105 Q 95 102 110 105 T 140 105 L 140 112 L 80 112 Z" fill={accent} opacity="0.95" filter={`url(#glow-${uid})`}>
          <animate attributeName="d" values="M 80 105 Q 95 102 110 105 T 140 105 L 140 112 L 80 112 Z;M 80 105 Q 95 108 110 105 T 140 105 L 140 112 L 80 112 Z;M 80 105 Q 95 102 110 105 T 140 105 L 140 112 L 80 112 Z" dur="3s" repeatCount="indefinite" />
        </path>
        {/* Bubbles */}
        {bubbles.map((b, i) => (
          <circle key={i} cx={b.x} r={b.size} fill="#FFFFFF" opacity="0.65">
            <animate attributeName="cy" values="168;107" dur={`${b.dur}s`} begin={`${b.delay}s`} repeatCount="indefinite" />
            <animate attributeName="opacity" values="0;0.75;0.75;0" keyTimes="0;0.1;0.85;1" dur={`${b.dur}s`} begin={`${b.delay}s`} repeatCount="indefinite" />
          </circle>
        ))}
        {/* Inner luminescence */}
        <ellipse cx="110" cy="140" rx="22" ry="30" fill={accent} opacity="0.45" filter={`url(#bigglow-${uid})`} />
      </g>

      {/* === LAYER 9: Glass specular highlights === */}
      <rect x="84" y="62" width="3" height="100" rx="1.5" fill="#FFFFFF" opacity="0.22" />
      <rect x="90" y="65" width="1.5" height="70" rx="0.75" fill="#FFFFFF" opacity="0.14" />
      <rect x="135" y="80" width="1.5" height="60" rx="0.75" fill="#FFFFFF" opacity="0.08" />

      {/* Light streak crossing diagonally */}
      <rect x="0" y="80" width="220" height="10" fill={`url(#shine-${uid})`} opacity="0.6" transform="rotate(-25 110 110)">
        <animate attributeName="y" values="40;180;40" dur="7s" repeatCount="indefinite" />
      </rect>

      {/* === LAYER 10: Label etched on vial === */}
      <text
        x="110"
        y="155"
        textAnchor="middle"
        fontFamily="ui-monospace, 'JetBrains Mono', monospace"
        fontSize={init.length >= 3 ? "9" : "10"}
        fontWeight="700"
        fill="#FFFFFF"
        opacity="0.9"
        letterSpacing="2"
        filter={`url(#glow-${uid})`}
      >
        {init || "PEP"}
      </text>
      <text x="110" y="164" textAnchor="middle" fontFamily="ui-monospace, monospace" fontSize="3.5" fill="#FFFFFF" opacity="0.55" letterSpacing="1">
        LOT · {String(seed).padStart(3, "0")}
      </text>

      {/* Volume markings on side */}
      <g stroke="#FFFFFF" strokeWidth="0.5" opacity="0.35">
        {[120, 130, 140, 150, 160].map((y) => (
          <line key={y} x1="82" x2="86" y1={y} y2={y} />
        ))}
      </g>
    </svg>
  );
};

export default VialSvg;
